import React from "react";
import { Sparkle, Compass, Target, CheckCircle, Calendar, TrendUp, ArrowsClockwise, Lightning as Bolt } from "@phosphor-icons/react";

const PRIORITY_TONE = {
  High: "bg-red-50 border-red-200 text-red-700",
  Medium: "bg-amber-50 border-amber-200 text-amber-700",
  Low: "bg-emerald-50 border-emerald-200 text-emerald-700",
};

const HORIZON_TONE = {
  "30 days": "bg-[#0B2A5B] text-white",
  "60 days": "bg-[#1E40AF] text-white",
  "90 days": "bg-[#A8B86C] text-white",
};

export default function ScopeOfImprovement({ data, loading, onRefresh, refreshing }) {
  const cached = data?._cached;
  return (
    <div
      className="rounded-lg border border-[#A8B86C]/40 bg-gradient-to-br from-white via-[#FAFBF5] to-[#F2F4E6] p-6 relative overflow-hidden"
      data-testid="scope-of-improvement-panel"
    >
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#0B2A5B] via-[#1E40AF] to-[#A8B86C] ai-shimmer-bar" />
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#0B2A5B] text-white flex items-center justify-center shrink-0 shadow-sm">
          <Compass size={22} weight="fill" className="ai-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Geist" }}>
              AI Scope of Improvement
            </h3>
            <span className="text-[10px] uppercase tracking-wider text-[#0B2A5B] bg-blue-100 border border-blue-200 px-1.5 py-0.5 rounded font-semibold">
              <Sparkle size={9} weight="fill" className="inline mr-0.5" /> For You
            </span>
            {cached && !loading && (
              <span
                className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded flex items-center gap-1"
                title="Served from cache — refreshes automatically when observations change"
                data-testid="scope-cached-badge"
              >
                <Bolt size={9} weight="fill" /> Cached
              </span>
            )}
            {onRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={refreshing || loading}
                className="ml-auto p-1 rounded hover:bg-[#A8B86C]/20 text-[#0B2A5B] disabled:opacity-50"
                title="Re-run AI plan"
                data-testid="scope-refresh-btn"
              >
                <ArrowsClockwise size={14} className={refreshing ? "animate-spin" : ""} />
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            A personalised 30/60/90-day plan generated from your open observations.
          </p>

          {loading ? (
            <div className="space-y-2 mt-4" data-testid="scope-loading">
              <div className="h-3 bg-[#A8B86C]/20 rounded ai-shimmer-bar w-3/4" />
              <div className="h-3 bg-[#A8B86C]/20 rounded ai-shimmer-bar w-2/3" />
              <div className="h-3 bg-[#A8B86C]/20 rounded ai-shimmer-bar w-1/2" />
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-800 mt-3 leading-relaxed" data-testid="scope-summary">
                {data?.summary || "No active observations to build an improvement plan from yet."}
              </p>

              {!!data?.focus_areas?.length && (
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-2">
                    <Target size={11} /> Focus Areas
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {data.focus_areas.map((f, i) => (
                      <div
                        key={`fa-${i}-${f.area}`}
                        className={`rounded-md border p-2.5 ${PRIORITY_TONE[f.priority] || PRIORITY_TONE.Medium}`}
                        data-testid={`scope-focus-${i}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-xs font-semibold truncate">{f.area}</div>
                          <span className="text-[9px] uppercase tracking-wider font-bold ml-1 shrink-0">
                            {f.priority}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-700 leading-snug">{f.why}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!!data?.action_plan?.length && (
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-2">
                    <Calendar size={11} /> 30 / 60 / 90 Day Action Plan
                  </div>
                  <ul className="space-y-2">
                    {data.action_plan.map((a, i) => (
                      <li
                        key={`ap-${i}-${a.title}`}
                        className="flex gap-2.5 items-start bg-white border border-slate-200 rounded-md p-2.5 lift-on-hover"
                        data-testid={`scope-action-${i}`}
                      >
                        <span
                          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded shrink-0 ${
                            HORIZON_TONE[a.horizon] || "bg-slate-200 text-slate-700"
                          }`}
                        >
                          {a.horizon}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                          <div className="text-xs text-slate-600 leading-snug mt-0.5">{a.detail}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {data?.business_value && (
                <div className="mt-5 flex items-start gap-2 bg-[#0B2A5B] text-white rounded-md p-3" data-testid="scope-business-value">
                  <TrendUp size={14} weight="fill" className="mt-0.5 text-[#A8B86C] shrink-0" />
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-blue-200 font-semibold">
                      Business Value
                    </div>
                    <p className="text-sm text-white mt-0.5">{data.business_value}</p>
                  </div>
                </div>
              )}

              {data?.encouragement && (
                <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700 italic" data-testid="scope-encouragement">
                  <CheckCircle size={13} weight="fill" /> {data.encouragement}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
